import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { PickersTextField } from "@mui/x-date-pickers/PickersTextField";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { ReactComponent as CalendarIcon } from "../../assets/images/calendar-icon.svg";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { MuiBox } from "../box/MuiBox";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiDateFieldWrapper } from "./MuiDateField.style";
import { MuiInfoToolTip } from "../tooltip/infoToolTip/MuiInfoToolTip";

export const OpenPickerIcon: React.FC<{
  hideCalendarIconOnValue?: boolean;
}> = ({ hideCalendarIconOnValue }) => {
  if (hideCalendarIconOnValue) return null;
  return <CalendarIcon />;
};

export interface MuiDateFieldProps extends DatePickerProps<Dayjs, boolean> {
  onChange?: (newValue: dayjs.Dayjs | null) => void;
  id?: string;
  label?: string;
  isMandatory?: boolean;
  disableLabel?: boolean;
  isErrorIconEnabled?: boolean;
  valueFormat?: string;
  clearable?: boolean;
  placeholder?: string;
  requiredARIALabel: string;
  selectDateARIALabel: string;
  /** If true, hides the calendar icon when a date value is selected */
  hideCalendarIconOnValue?: boolean;
  infoMessage?: string;
}

export const MuiDateField: React.FC<MuiDateFieldProps> = ({
  onChange,
  id = COMPONENT_IDS.COMMON_DATE_PICKER_FORM_CONTROLLER,
  label,
  isMandatory,
  disableLabel,
  isErrorIconEnabled = true,
  clearable = false,
  placeholder = "",
  requiredARIALabel = "Required",
  selectDateARIALabel = "Select",
  hideCalendarIconOnValue,
  infoMessage,
  ...rest
}) => {
  return (
    <MuiDateFieldWrapper>
      {label && (
        <MuiBox className={"title"}>
          <MuiTypography
            className={disableLabel ? "mui-label disabled" : "mui-label"}
            component="label"
            variant="h3"
          >
            {label}{" "}
            {isMandatory && (
              <label aria-label={requiredARIALabel} className="asterisk">
                *
              </label>
            )}
          </MuiTypography>
          {infoMessage && (
            <MuiBox className="tooltip-container">
              <MuiInfoToolTip
                message={infoMessage}
                placement="top"
                className="tooltip-wrapper"
              />
            </MuiBox>
          )}
        </MuiBox>
      )}
      <DatePicker
        {...(placeholder && { label: placeholder })}
        onChange={onChange}
        enableAccessibleFieldDOMStructure
        slots={{
          textField: PickersTextField,
          openPickerIcon: OpenPickerIcon,
        }}
        slotProps={{
          field: {
            clearable,
          },
          textField: {
            id: id,
            inputProps: {
              "data-testid": id,
              "aria-label": selectDateARIALabel,
            },
          },
          popper: {
            "aria-label": selectDateARIALabel,
          },
          openPickerIcon: { hideCalendarIconOnValue },
        }}
        {...rest}
      />
    </MuiDateFieldWrapper>
  );
};
